import React, { useEffect, useRef } from "react";
import useEscapeLayer from "../useEscape";

/*
 * BOTTOM SHEET: the phone's popover, docked where the thumb is.
 *
 * On the phone the rail groups, the More menu and Change status… open as sheets
 * over the record (the brief, Layout › Phone: "Rail groups open as bottom
 * sheets"), never as a popover a 375px screen would clip. Same card grammar as
 * the panel: the black rule on top, a sentence-case title, rows at least 44px so
 * a thumb can hit them, and nothing yellow inside (what opened it spent the
 * yellow). It sits above the record's own full-screen sheet (record.css: z 75/76
 * over 65) and under a confirm dialog (80); the backdrop, the × and Esc close it,
 * one layer at a time (useEscape), so a sheet opened from a sheet closes first.
 *
 * Focus moves into the sheet when it opens, so a keyboard or a screen reader is
 * where the content is, and goes back to what opened it on close.
 *
 * Props:
 *   open      boolean; nothing renders while false
 *   title     string, the sheet's heading ("Deal and money", "More")
 *   onClose   () => void, from the backdrop, the × and Esc
 *   children  the body
 */
let seq = 0;

export default function BottomSheet({ open, title = null, onClose, children }) {
  const sheetRef = useRef(null);
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = `rec-sheet-${++seq}`;
  const close = () => onClose && onClose();
  useEscapeLayer(!!open, close);

  // What had the focus when the sheet opened, read while rendering the opening:
  // by the time this component's effect runs, a child (Change status… focuses
  // itself) may already have taken it.
  const before = useRef(null);
  const wasOpen = useRef(false);
  if (open && !wasOpen.current && typeof document !== "undefined") before.current = document.activeElement;
  wasOpen.current = !!open;

  useEffect(() => {
    if (!open) return undefined;
    const el = sheetRef.current;
    if (el && !el.contains(document.activeElement)) el.focus({ preventScroll: true });
    return () => {
      const back = before.current;
      if (back && typeof back.focus === "function" && back.isConnected) back.focus({ preventScroll: true });
    };
  }, [open]);

  if (!open) return null;
  return (
    <>
      <div className="rec-sheet__backdrop" onClick={close} aria-hidden="true" />
      <div className="rec-sheet" role="dialog" aria-modal="true" aria-labelledby={title ? idRef.current : undefined} aria-label={title ? undefined : "Sheet"} tabIndex={-1} ref={sheetRef}>
        <div className="rec-sheet__grip" aria-hidden="true" />
        <div className="rec-sheet__head">
          <span className="rec-sheet__title" id={idRef.current}>{title}</span>
          <button type="button" className="rec-close" title="Close (Esc)" aria-label="Close" onClick={close}>×</button>
        </div>
        <div className="rec-sheet__body">{children}</div>
      </div>
    </>
  );
}
